import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface DatabaseConfig {
  id: string;
  name: string;
  type: 'supabase' | 'mysql' | 'postgresql';
  config: {
    url?: string;
    anonKey?: string;
    host?: string;
    port?: number;
    database?: string;
    username?: string;
    password?: string;
  };
  is_active: boolean;
  owner_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export const useSecureDatabaseConfig = () => {
  const [configs, setConfigs] = useState<DatabaseConfig[]>([]);
  const [currentConfig, setCurrentConfig] = useState<DatabaseConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Load configs from Supabase instead of localStorage
  const loadConfigs = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('database_configs')
        .select('*')
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Type cast the data properly
      const typedData = (data || []).map(item => ({
        ...item,
        type: item.type as 'supabase' | 'mysql' | 'postgresql',
        config: item.config as DatabaseConfig['config']
      }));

      setConfigs(typedData);
      
      // Find active config
      const activeConfig = typedData.find(config => config.is_active);
      if (activeConfig) {
        setCurrentConfig(activeConfig);
      }
    } catch (error) {
      console.error('Error loading database configs:', error);
      toast.error('فشل تحميل إعدادات قاعدة البيانات');
    } finally {
      setLoading(false);
    }
  };

  // Save config to Supabase
  const saveConfig = async (newConfig: Omit<DatabaseConfig, 'id' | 'owner_id' | 'created_by' | 'created_at' | 'updated_at'>) => {
    if (!user?.id) {
      toast.error('يجب تسجيل الدخول أولاً');
      return false;
    }

    try {
      const { data, error } = await supabase
        .from('database_configs')
        .insert({
          name: newConfig.name,
          type: newConfig.type,
          config: newConfig.config,
          is_active: newConfig.is_active,
          owner_id: user.id,
          created_by: user.id
        })
        .select()
        .single();

      if (error) throw error;

      await loadConfigs();
      toast.success('تم حفظ الإعدادات بنجاح');
      return true;
    } catch (error) {
      console.error('Error saving database config:', error);
      toast.error('فشل حفظ الإعدادات');
      return false;
    }
  };

  // Switch to a different config
  const switchToConfig = async (configId: string) => {
    if (!user?.id) {
      toast.error('يجب تسجيل الدخول أولاً');
      return;
    }

    try {
      // First, deactivate all configs
      await supabase
        .from('database_configs')
        .update({ is_active: false })
        .eq('owner_id', user.id);

      // Then activate the selected one
      const { error } = await supabase
        .from('database_configs')
        .update({ is_active: true })
        .eq('id', configId)
        .eq('owner_id', user.id);

      if (error) throw error;

      await loadConfigs();
      toast.success('تم التبديل إلى الإعداد المحدد');
      
      // Reload the page to apply new config
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error switching database config:', error);
      toast.error('فشل التبديل إلى الإعداد المحدد');
    }
  };

  // Delete a config
  const deleteConfig = async (configId: string) => {
    if (!user?.id) {
      toast.error('يجب تسجيل الدخول أولاً');
      return;
    }

    try {
      const { error } = await supabase
        .from('database_configs')
        .delete()
        .eq('id', configId)
        .eq('owner_id', user.id);

      if (error) throw error;

      await loadConfigs();
      toast.success('تم حذف الإعداد بنجاح');
    } catch (error) {
      console.error('Error deleting database config:', error);
      toast.error('فشل حذف الإعداد');
    }
  };

  // Migrate existing localStorage configs to Supabase (one-time migration)
  const migrateFromLocalStorage = async () => {
    if (!user?.id) return;

    const localConfigs = localStorage.getItem('database_configs');
    if (localConfigs) {
      try {
        const parsedConfigs = JSON.parse(localConfigs);
        
        for (const config of parsedConfigs) {
          await saveConfig({
            name: config.name,
            type: config.type,
            config: config.config,
            is_active: config.isActive || false
          });
        }
        
        // Clear localStorage after successful migration
        localStorage.removeItem('database_configs');
        localStorage.removeItem('active_database_config');
        
        toast.success('تم ترحيل الإعدادات بنجاح');
      } catch (error) {
        console.error('Error migrating configs:', error);
      }
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadConfigs().then(() => {
        // Only migrate if no configs exist in Supabase
        if (configs.length === 0) {
          migrateFromLocalStorage();
        }
      });
    }
  }, [user?.id]);

  return {
    configs,
    currentConfig,
    loading,
    saveConfig,
    switchToConfig,
    deleteConfig,
    refreshConfigs: loadConfigs
  };
};